import { useEffect, useRef } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { trackResourceViewed } from '@/lib/analytics/events';
import type { PageMetaLoaderData } from '@/lib/analytics/page-meta';
import { useMember } from '@/lib/use-session';
import { resourcesKeys, useResourceDetail } from '@/lib/queries/resources';
import { loadForRoute } from '@/lib/queries/load-for-route';
import { editorialTitleSubject } from '@/lib/seo-text';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { ResourceArticleView } from './-components/ResourceArticleView';

export const Route = createFileRoute('/_public/resources/$id/')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  loader: async ({ params, context: { queryClient } }): Promise<PageMetaLoaderData> => {
    const resource = await loadForRoute(queryClient, {
      ...resourcesKeys.detail(params.id),
      retry: false,
    });
    return resource === undefined ? {} : { pageTitle: editorialTitleSubject(resource) };
  },
  pendingComponent: ResourceDetailPending,
  component: ResourceDetailPage,
});

function ResourceDetailPending() {
  return (
    <ResourceArticleView
      resource={null}
      related={null}
      signedIn={false}
      viewerIsAdmin={false}
      viewerCanEdit={false}
    />
  );
}

function ResourceDetailPage() {
  const { id } = Route.useParams();
  const me = useMember();

  const resourceQuery = useResourceDetail(id, { retry: false, retryOnMount: false });
  const resource = resourceQuery.data ?? null;
  const relatedQuery = useQuery({
    ...resourcesKeys.related(id, { tagSlug: resource?.tags[0]?.slug }),
    enabled: resource !== null,
  });

  const viewedResourceIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (resource === null || viewedResourceIdRef.current === resource.id) return;
    viewedResourceIdRef.current = resource.id;
    trackResourceViewed({ content_type: resource.kind });
  }, [resource]);

  // 404 (never existed / not visible) and 410 GONE (public resource since
  // deleted) both arrive as NotFoundApiError → branded NotFoundPage
  // directly (render-context `throw notFound()` lands in TanStack's
  // catch-boundary, not its 404 surface).
  if (isNotFoundApiError(resourceQuery.error)) return <NotFoundPage />;
  if (resourceQuery.isError) throw notFound();

  const signedIn = me !== null;
  const viewerIsAdmin = me !== null && me.platformRole === 'admin';
  const viewerCanEdit = viewerIsAdmin;

  return (
    <ResourceArticleView
      resource={resource}
      related={relatedQuery.data ?? null}
      signedIn={signedIn}
      viewerIsAdmin={viewerIsAdmin}
      viewerCanEdit={viewerCanEdit}
    />
  );
}
